import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Filter, X } from "lucide-react";
import axiosClient from "../../global/Globalapi";
import { getData } from "../../global/Globalapi";

const FilterSidebar = ({ onFilterChange }) => {
  const [filters, setFilters] = useState({});
  const [openSections, setOpenSections] = useState({});
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch dynamic filter options from backend
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setLoading(true);
        const response = await getData('/filter-options');
        console.log('Filter options response:', response);
        
        if (response.success && response.filters) {
          setFilterOptions(response.filters);
        } else if (response.data && response.data.filters) {
          setFilterOptions(response.data.filters);
        } else {
          console.error('Unexpected filter options format:', response);
          setFilterOptions(null);
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
        setFilterOptions(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFilterOptions();
  }, []);

  const toggleSection = (type) => {
    setOpenSections((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const handleCheckboxChange = (type, value) => {
    setFilters((prev) => {
      const current = prev[type] || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const newFilters = { ...prev, [type]: updated };
      
      console.log('🔄 Checkbox changed:', type, value);
      console.log('📊 New filters:', newFilters);
      
      onFilterChange(newFilters);
      return newFilters;
    });
  };

  // Handle price range selection with slider
  const handlePriceRangeChange = (min, max) => {
    console.log('💰 Price range selected:', { min, max });
    
    const newFilters = { 
      ...filters, 
      price: { min, max } 
    };
    
    console.log('📊 New filters with price:', newFilters);
    
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  // Clear price filter
  const clearPriceFilter = () => {
    console.log('🗑️ Clearing price filter');
    
    const newFilters = { ...filters };
    delete newFilters.price;
    
    console.log('📊 Filters after clearing price:', newFilters);
    
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  if (loading) {
    return (
      <div className="w-72 bg-white shadow-xl rounded-xl p-5 border border-gray-200">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-24"></div>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-32"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!filterOptions) {
    return (
      <div className="w-72 bg-white shadow-xl rounded-xl p-5 border border-gray-200">
        <p className="text-red-500">Failed to load filters</p>
      </div>
    );
  }

  const filterConfig = [
    {
      type: "category",
      title: "Category",
      options: Array.isArray(filterOptions.categories) 
        ? filterOptions.categories.map(c => ({
            label: c.name || c,
            value: c.id || c
          }))
        : [],
    },
    {
      type: "price",
      title: "Price Range",
      range: {
        min: filterOptions.price?.min || 0,
        max: filterOptions.price?.max || 10000
      }
    },
    {
      type: "color",
      title: "Colour",
      options: Array.isArray(filterOptions.colors) ? filterOptions.colors : [],
    },
    {
      type: "discount",
      title: "Discount Amount",
      options: Array.isArray(filterOptions.discounts) ? filterOptions.discounts : [],
    },
  ];

  const SidebarContent = () => (
    <div className="w-72 bg-white shadow-xl rounded-xl p-5 space-y-6 border border-gray-200 h-full overflow-y-auto">
      <h2 className="text-xl font-bold mb-2">Filters</h2>

      {filterConfig.map((section) => {
        const isOpen = openSections[section.type] ?? true;
        return (
          <div
            key={section.type}
            className="border-b border-gray-200 pb-3 last:border-none"
          >
            {/* Section Header */}
            <div
              className="flex justify-between items-center cursor-pointer mb-2"
              onClick={() => toggleSection(section.type)}
            >
              <h3 className="font-semibold text-gray-800">{section.title}</h3>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-600" />
              )}
            </div>

            {/* Section Content */}
            {isOpen && (
              <div className="pl-1 space-y-2">
                {section.type === "price" ? (
                  <div className="space-y-4 py-2">
                    {/* Price Range Slider */}
                    <div className="px-2">
                      <div className="flex justify-between text-xs text-gray-600 mb-2">
                        <span>₹{filters.price?.min?.toLocaleString('en-IN') || section.range.min.toLocaleString('en-IN')}</span>
                        <span>₹{filters.price?.max?.toLocaleString('en-IN') || section.range.max.toLocaleString('en-IN')}</span>
                      </div>
                      
                      {/* Min Price Slider */}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 mb-1 block">Min Price</label>
                        <input
                          type="range"
                          min={section.range.min}
                          max={section.range.max}
                          value={filters.price?.min || section.range.min}
                          onChange={(e) => {
                            const min = parseInt(e.target.value);
                            const max = filters.price?.max || section.range.max;
                            if (min <= max) {
                              handlePriceRangeChange(min, max);
                            }
                          }}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                      </div>
                      
                      {/* Max Price Slider */}
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Max Price</label>
                        <input
                          type="range"
                          min={section.range.min}
                          max={section.range.max}
                          value={filters.price?.max || section.range.max}
                          onChange={(e) => {
                            const max = parseInt(e.target.value);
                            const min = filters.price?.min || section.range.min;
                            if (max >= min) {
                              handlePriceRangeChange(min, max);
                            }
                          }}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                      </div>
                    </div>

                    {/* Clear price filter button */}
                    {filters.price && (
                      <button
                        onClick={clearPriceFilter}
                        className="w-full text-xs text-white bg-red-500 hover:bg-red-600 px-3 py-2 rounded-lg font-medium transition"
                      >
                        RESET PRICE RANGE
                      </button>
                    )}
                  </div>
                ) : section.type === "color" ? (
                  <div className="flex flex-wrap gap-2 py-2">
                    {section.options.map((color) => {
                      const isSelected = filters[section.type]?.includes(color);
                      
                      return (
                        <button
                          key={color}
                          onClick={() => handleCheckboxChange(section.type, color)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            isSelected 
                              ? 'bg-blue-600 text-white shadow-md scale-105' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {color}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {section.options.map((opt) => {
                      const value = typeof opt === 'object' ? opt.value : opt;
                      const label = typeof opt === 'object' ? opt.label : opt;
                      const isSelected = filters[section.type]?.includes(value) || false;
                      
                      return (
                        <label
                          key={value}
                          className="flex items-center space-x-2 cursor-pointer group"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleCheckboxChange(section.type, value)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                          />
                          <span className={`text-sm transition ${
                            isSelected 
                              ? 'text-blue-600 font-semibold' 
                              : 'text-gray-700 group-hover:text-blue-600'
                          }`}>
                            {label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Active Filters Display */}
      {Object.keys(filters).length > 0 && (
        <div className="pt-3 border-t border-gray-200">
          <h4 className="text-sm font-semibold mb-2">Active Filters:</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(filters).map(([type, values]) => {
              if (type === 'price') {
                return (
                  <span key={type} className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full font-medium">
                    ₹{values.min.toLocaleString('en-IN')} - ₹{values.max.toLocaleString('en-IN')}
                  </span>
                );
              }
              if (Array.isArray(values)) {
                return values.map(value => (
                  <span key={`${type}-${value}`} className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full font-medium">
                    {value}
                  </span>
                ));
              }
              return null;
            })}
          </div>
        </div>
      )}

      {/* Reset Filters Button */}
      {Object.keys(filters).length > 0 && (
        <button
          onClick={() => {
            console.log('🗑️ Clearing all filters');
            setFilters({});
            onFilterChange({});
          }}
          className="w-full bg-red-500 hover:bg-red-600 text-white text-sm py-2 rounded-lg shadow-md transition font-semibold"
        >
          Clear All Filters
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile Hamburger */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="md:hidden fixed top-20 left-4 z-50 bg-blue-600 text-white p-3 rounded-lg shadow-lg"
      >
        <Filter className="h-5 w-5" />
      </button>

      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <SidebarContent />
      </div>

      {/* Mobile Sidebar Drawer */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setIsMobileOpen(false)}
          ></div>

          <div className="relative w-80 bg-white shadow-xl h-full z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
            <button
              onClick={() => setIsMobileOpen(false)}
              className="absolute top-3 right-3 text-gray-600 hover:text-black z-10"
            >
              <X className="h-6 w-6" />
            </button>

            <SidebarContent />
          </div>
        </div>
      )}
    </>
  );
};

export default FilterSidebar;